import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ChevronRight, Search, X } from "lucide-react";
import heroImg from "@/assets/men-and-trauma.jpg";
import { MEN_INTRO } from "@/data/learn/men-and-trauma";
import { MEN_TOOLS } from "@/data/learn/men-tools";
import { MenCrisisNote } from "@/components/men/MenExercises";
import { Button } from "@/components/ui/button";

const TITLE = "Men & Trauma — Recovery Trauma™";
const DESC =
  "How trauma, stress and difficult experiences can affect men's emotions, bodies, relationships and everyday lives. Non-judgemental, non-diagnostic education.";

export const Route = createFileRoute("/learn/men-and-trauma/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/men-and-trauma" }],
  }),
  component: MenLanding,
});

type Card = { emoji: string; title: string; desc: string; slug?: string; to?: string };
type Group = { title: string; desc: string; wash: string; chip: string; cards: Card[] };

const GROUPS: Group[] = [
  {
    title: "Understanding Yourself",
    desc: "How distress can show up in men's lives — often without ever being named — and what may be driving it.",
    wash: "bg-gradient-to-br from-amber-50 via-card to-amber-100/70 dark:from-amber-950/30 dark:via-card dark:to-amber-900/20 border-amber-200/60 dark:border-amber-800/40",
    chip: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
    cards: [
      { emoji: "🧠", title: "How Trauma Can Show Up in Men", desc: "Anger, numbness, being constantly on guard, trouble sleeping, pulling away from people — the everyday ways distress can surface in men without anyone calling it trauma.", slug: "how-trauma-can-show-up" },
      { emoji: "🔥", title: "Anger & What's Underneath It", desc: "Irritability, snapping, blowing up or going cold. Fear, shame, hurt and feeling disrespected can sit underneath anger — and there are safer ways to release the pressure.", slug: "anger-and-trauma" },
      { emoji: "🧊", title: "Emotional Shutdown & Numbness", desc: "Feeling flat, empty or nothing at all. Why the nervous system turns feelings down after too much overload — and how connection with feeling can slowly return.", slug: "emotional-shutdown-and-numbness" },
      { emoji: "🛡️", title: "Fight, Flight, Freeze & Fawn", desc: "Fighting back, escaping, going silent or keeping everyone happy. Recognise your own protective patterns — then map them on the Trauma Response Wheel.", slug: "fight-flight-freeze-fawn" },
      { emoji: "🧍", title: "“I'll Handle It Myself”", desc: "Hating to ask for help, needing to stay in control, only resting when everything's done. Where hyper-independence often begins — and what it costs.", slug: "hyper-independence" },
    ],
  },
  {
    title: "Your Past",
    desc: "Early experiences can keep shaping adult life — self-worth, trust, boundaries and relationships.",
    wash: "bg-gradient-to-br from-stone-50 via-card to-stone-200/60 dark:from-stone-900/40 dark:via-card dark:to-stone-800/30 border-stone-200/70 dark:border-stone-700/50",
    chip: "bg-stone-200/70 text-stone-800 dark:bg-stone-800/60 dark:text-stone-200",
    cards: [
      { emoji: "👦", title: "Childhood Trauma & the Adult Man", desc: "Criticism, neglect, violence or feeling unsafe as a boy can follow into manhood — how it can shape self-worth, trust, anger and intimacy, and what helps.", slug: "childhood-trauma-and-the-adult-man" },
      { emoji: "👩‍👦", title: "Growing Up With an Unsafe or Abusive Mother", desc: "Criticism, control, guilt, parentification and love that felt conditional — plus practical support for boundaries, guilt and healing as an adult man.", slug: "abusive-or-emotionally-unsafe-mother" },
      { emoji: "👨‍👦", title: "Growing Up With an Unsafe or Abusive Father", desc: "Fear, violence, emotional absence or pressure to earn approval — how a frightening father can shape the man you became, and how change is possible.", slug: "abusive-or-emotionally-unsafe-father" },
      { emoji: "🏠", title: "Male Survivors of Abuse", desc: "Emotional, physical, sexual and domestic abuse happens to men too — far more often than is talked about. Without shame, and without minimising what happened.", slug: "male-survivors-of-abuse" },
      { emoji: "🤐", title: "Male Sexual Trauma", desc: "Confusion, shame, unwanted reactions, telling someone or keeping silent. Clear, calm information for men who experienced sexual abuse or assault.", slug: "sexual-trauma-in-men" },
    ],
  },
  {
    title: "Relationships & Life",
    desc: "Closeness, fatherhood, masculinity and the everyday weight men carry.",
    wash: "bg-gradient-to-br from-sky-50 via-card to-sky-100/70 dark:from-sky-950/30 dark:via-card dark:to-sky-900/20 border-sky-200/60 dark:border-sky-800/40",
    chip: "bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200",
    cards: [
      { emoji: "❤️", title: "Men, Love & Relationships", desc: "Why closeness can feel threatening: attachment, trust, arguments that escalate or go silent, and why certain moments with a partner trigger strong reactions.", slug: "relationships-and-intimacy" },
      { emoji: "👨‍👧‍👦", title: "Fathers & Trauma", desc: "Wanting to be different from your own father. Triggers, patience, emotional availability and the fear of repeating the past with your own children.", slug: "fathers-and-trauma" },
      { emoji: "🎭", title: "Masculinity, Shame & Vulnerability", desc: "“Man up.” “Don't cry.” “Be strong.” How those messages make feelings unsafe — and can make asking for help feel like failure.", slug: "masculinity-shame-and-asking-for-help" },
      { emoji: "🌙", title: "Men's Loneliness", desc: "Surrounded by people and still alone. Why male friendship often thins out with age — and how connection can be rebuilt slowly, without forcing it.", slug: "loneliness-and-isolation" },
      { emoji: "💼", title: "Always Busy", desc: "Work, gym, gaming, phone — always something. The difference between healthy activity and avoidance wearing a useful disguise.", slug: "work-overworking-and-avoidance" },
    ],
  },
  {
    title: "Healing & Support",
    desc: "Understanding what's happening — and finding support that fits you.",
    wash: "bg-gradient-to-br from-emerald-50 via-card to-emerald-100/70 dark:from-emerald-950/30 dark:via-card dark:to-emerald-900/20 border-emerald-200/60 dark:border-emerald-800/40",
    chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
    cards: [
      { emoji: "🧠", title: "PTSD & Complex Trauma", desc: "Nightmares, flashbacks, being on alert, avoiding reminders. Clear education about PTSD and complex trauma in men — information, not self-diagnosis.", slug: "ptsd-and-complex-trauma" },
      { emoji: "🍺", title: "Coping & Escape", desc: "Alcohol, drugs, gambling, risk-taking. Why numbing can make sense in the moment, what it costs over time, and where judgement-free support exists.", slug: "coping-through-alcohol-drugs-or-risk" },
      { emoji: "📋", title: "Men's Trauma & Wellbeing Check-In", desc: "Twelve everyday areas — anger, sleep, tension, relationships, drinking, work. A private self-reflection showing which areas may need care. Not a test, not a diagnosis.", to: "/learn/men-and-trauma/check-in" },
    ],
  },
];

type StartCard = {
  emoji: string;
  title: string;
  desc: string;
  cta: string;
  slug?: string;
  to?: string;
};

const START_CARDS: StartCard[] = [
  { emoji: "🔥", title: "I feel angry or on edge", desc: "Explore what may be happening underneath the anger and find practical ways to release tension safely.", cta: "Explore Anger & Regulation", slug: "anger-and-trauma" },
  { emoji: "🧊", title: "I shut down or feel numb", desc: "You might find it difficult to explain what you're feeling — or feel very little at all.", cta: "Explore Shutdown & Numbness", slug: "emotional-shutdown-and-numbness" },
  { emoji: "🧍", title: "I deal with everything myself", desc: "If asking for help feels uncomfortable or you feel you should be able to handle everything alone, explore hyper-independence and where it can come from.", cta: "Explore “I'll Handle It Myself”", slug: "hyper-independence" },
  { emoji: "🧠", title: "I don't know what I'm feeling", desc: "You don't need to name it immediately. Use the Men's Check-In to explore what's been happening in your emotions, body, relationships and everyday life.", cta: "Start Men's Check-In", to: "/learn/men-and-trauma/check-in" },
  { emoji: "🛡️", title: "I want to understand my reactions", desc: "Do you fight back, leave, shut down, avoid conflict or automatically try to keep everyone happy?", cta: "Explore the Trauma Response Wheel", to: "/trauma-response-wheel" },
  { emoji: "❤️", title: "My relationships keep getting difficult", desc: "Explore trust, vulnerability, attachment, intimacy, conflict and why certain situations can trigger strong reactions.", cta: "Explore Men & Relationships", slug: "relationships-and-intimacy" },
  { emoji: "👦", title: "My childhood still affects me", desc: "How experiences with parents, criticism, abuse, neglect or feeling emotionally unsafe growing up may continue to influence adult life.", cta: "Explore Childhood Trauma", slug: "childhood-trauma-and-the-adult-man" },
  { emoji: "🧰", title: "I don't want to talk — give me something practical", desc: "That's okay. Start with something you can actually do: The Tool Shop, Wall Push, Cool Water Grounding, Grounding or Breathing Exercises.", cta: "Try a Practical Tool", to: "/learn/men-and-trauma/tools" },
  { emoji: "🌙", title: "I'm exhausted but can't switch off", desc: "Explore sleep, hypervigilance, stress and simple regulation tools for when your body feels tired but your mind stays active.", cta: "Help Me Switch Off", to: "/sleep" },
];

const startCardCls =
  "group flex h-[184px] w-[76%] shrink-0 snap-start flex-col rounded-2xl border border-border bg-card p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/60 hover:shadow-lg sm:h-auto sm:min-h-[220px] sm:w-[46%] lg:w-auto";

const cardCls =
  "group flex w-[80%] shrink-0 snap-start flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/60 hover:shadow-lg sm:w-[46%] lg:w-auto";

function ArticleRow({ c }: { c: Card }) {
  return c.slug ? (
    <Link to="/learn/men-and-trauma/$slug" params={{ slug: c.slug }} className="group flex h-[164px] w-full items-start gap-3 rounded-2xl border border-border/60 bg-card/85 p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md lg:h-auto">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-background/90 text-lg shadow-sm" aria-hidden>
        {c.emoji}
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="font-serif text-base leading-snug text-foreground sm:text-lg">{c.title}</h3>
        <p className="mt-1 line-clamp-4 text-sm leading-relaxed text-foreground/70 lg:line-clamp-none">{c.desc}</p>
      </div>
      <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-foreground" aria-hidden />
    </Link>
  ) : (
    <Link to={c.to ?? "/learn/men-and-trauma"} className="group flex h-[164px] w-full items-start gap-3 rounded-2xl border border-border/60 bg-card/85 p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md lg:h-auto">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-background/90 text-lg shadow-sm" aria-hidden>
        {c.emoji}
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="font-serif text-base leading-snug text-foreground sm:text-lg">{c.title}</h3>
        <p className="mt-1 line-clamp-4 text-sm leading-relaxed text-foreground/70 lg:line-clamp-none">{c.desc}</p>
      </div>
      <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-foreground" aria-hidden />
    </Link>
  );
}

const rowCls = "-mx-4 mt-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-3 lg:mx-0 lg:grid lg:grid-cols-3 lg:gap-4 lg:overflow-visible lg:px-0";

const TOOL_CARD_THEME: Record<string, { shell: string; action: string; label: string }> = {
  "tool-shop": {
    shell: "border-tool-terracotta/45 bg-tool-terracotta/10",
    action: "bg-tool-terracotta text-primary-foreground",
    label: "Out-of-home grounding",
  },
  "cool-water": {
    shell: "border-tool-water bg-tool-water/30",
    action: "bg-foreground text-background",
    label: "Sensory grounding",
  },
  "wall-push": {
    shell: "border-tool-sage bg-tool-sage/35",
    action: "bg-secondary-foreground text-background",
    label: "Physical grounding",
  },
};

function GroupCard({ g }: { g: Group }) {
  return (
    <section id={g.title.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and")} className={`mt-6 scroll-mt-28 rounded-2xl border py-4 shadow-sm sm:p-6 lg:mt-8 lg:rounded-3xl ${g.wash}`}>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-4 sm:px-0">
        <h2 className="min-w-0 truncate font-serif text-xl text-foreground sm:text-2xl">{g.title}</h2>
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${g.chip}`}>
          {g.cards.length} guides
        </span>
      </div>
      <p className="mt-1 px-4 text-sm leading-relaxed text-foreground/75 sm:px-0">{g.desc}</p>
      <ul className="mt-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 sm:px-0 lg:grid lg:grid-cols-2 lg:overflow-visible lg:pb-0 xl:grid-cols-3">
        {g.cards.map((c) => <li className="w-[82%] shrink-0 snap-start sm:w-[48%] lg:w-auto" key={c.title}><ArticleRow c={c} /></li>)}
      </ul>
    </section>
  );
}

type SearchResult = {
  key: string;
  emoji: string;
  title: string;
  desc: string;
  category: string;
  slug?: string;
  to?: string;
  hash?: string;
};

const SEARCH_ITEMS: SearchResult[] = [
  ...GROUPS.flatMap((group) => group.cards.map((card) => ({ ...card, key: `${group.title}-${card.title}`, category: group.title }))),
  ...START_CARDS.map((card) => ({ ...card, key: `start-${card.title}`, category: "Where Do I Start?" })),
  ...MEN_TOOLS.map((tool) => ({
    key: `tool-${tool.id}`,
    emoji: tool.emoji,
    title: tool.title,
    desc: tool.tagline,
    category: "Practical Tools",
    to: "/learn/men-and-trauma/tools",
    hash: tool.id,
  })),
];

function SearchResultLink({ item }: { item: SearchResult }) {
  const content = (
    <>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-muted text-base" aria-hidden>{item.emoji}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-semibold uppercase text-muted-foreground">{item.category}</span>
        <span className="block truncate font-serif text-base text-foreground">{item.title}</span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
    </>
  );

  const className = "flex min-h-[68px] items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 shadow-sm transition hover:border-primary/50";
  if (item.slug) {
    return <Link to="/learn/men-and-trauma/$slug" params={{ slug: item.slug }} className={className}>{content}</Link>;
  }
  return <Link to={item.to ?? "/learn/men-and-trauma"} hash={item.hash} className={className}>{content}</Link>;
}

function MenLanding() {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const results = useMemo(() => {
    if (!normalizedQuery) return [];
    return SEARCH_ITEMS.filter((item) =>
      `${item.title} ${item.desc} ${item.category}`.toLocaleLowerCase().includes(normalizedQuery),
    );
  }, [normalizedQuery]);

  return (
    <main className="mx-auto max-w-6xl px-4 pb-16 pt-6 sm:pt-10">
      <section className="relative overflow-hidden rounded-3xl border border-border">
        <img src={heroImg} alt="Four men of different ages and backgrounds talking together on a bench at sunset" width={1536} height={864} className="aspect-[4/3] w-full object-cover sm:aspect-[21/9]" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">Understand</p>
          <h1 className="mt-1 font-serif text-3xl text-foreground sm:text-5xl">Men & Trauma</h1>
          <p className="mt-1 text-base text-foreground/90 sm:text-xl">Understanding what happens underneath the surface.</p>
        </div>
      </section>

      <div className="mt-5 max-w-3xl space-y-2 text-foreground/90">
        <p>Trauma doesn't always look like sadness or fear. It can sometimes appear as anger, shutting down, staying constantly busy, feeling disconnected, struggling with relationships or believing you have to handle everything alone.</p>
        <details className="group">
          <summary className="inline-flex min-h-[44px] cursor-pointer items-center text-sm font-medium text-primary">
            <span className="group-open:hidden">Read more</span>
            <span className="hidden group-open:inline">Show less</span>
          </summary>
          <p>Explore how difficult experiences can affect emotions, relationships, the body and everyday life — and discover practical ways to support yourself.</p>
        </details>
      </div>

      <section aria-label="Search Men and Trauma" className="mt-4 rounded-2xl border border-border bg-background p-2 shadow-sm sm:max-w-3xl sm:p-3">
        <label htmlFor="men-trauma-search" className="sr-only">Search guides and practical tools</label>
        <div className="relative flex items-center">
          <Search className="pointer-events-none absolute left-3 h-5 w-5 text-muted-foreground" aria-hidden />
          <input
            id="men-trauma-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search guides and practical tools"
            className="min-h-12 w-full rounded-xl border border-border bg-muted/60 py-2 pl-10 pr-12 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
          {query ? (
            <Button type="button" variant="ghost" size="icon" onClick={() => setQuery("")} className="absolute right-1 h-10 w-10" aria-label="Clear search">
              <X className="h-5 w-5" />
            </Button>
          ) : null}
        </div>
      </section>

      {normalizedQuery ? (
        <section className="mt-4" aria-live="polite">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-serif text-xl text-foreground">Search results</h2>
            <span className="text-sm text-muted-foreground">{results.length} found</span>
          </div>
          {results.length ? (
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((item) => <SearchResultLink key={item.key} item={item} />)}
            </div>
          ) : (
            <div className="mt-3 rounded-xl border border-border bg-card p-5 text-center">
              <p className="font-medium text-foreground">No matching guides or tools</p>
              <p className="mt-1 text-sm text-muted-foreground">Try a shorter word, such as anger, sleep, relationships or grounding.</p>
              <Button type="button" variant="outline" onClick={() => setQuery("")} className="mt-4">Clear search</Button>
            </div>
          )}
        </section>
      ) : (
        <>

      {/* Where do I start */}
      <section className="mt-5 rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/15 via-card to-card py-4 sm:rounded-3xl sm:p-7">
        <div className="max-w-3xl">
          <h2 className="px-4 font-serif text-xl text-foreground sm:px-0 sm:text-2xl">🌱 Where Do I Start?</h2>
          <p className="mt-1 px-4 text-sm text-foreground/85 sm:px-0 sm:text-base">Choose what feels most familiar right now. You don't need the right words.</p>
        </div>
        <div className="mt-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 sm:px-0 lg:grid lg:grid-cols-3 lg:overflow-visible">
          {START_CARDS.map((c) => {
            const inner = (
              <>
                <span className="grid h-10 w-10 place-items-center rounded-full bg-background/80 text-lg shadow-sm" aria-hidden>{c.emoji}</span>
                <h3 className="mt-2.5 font-serif text-base leading-snug text-foreground">{c.title}</h3>
                 <p className="mt-1 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground sm:line-clamp-none">{c.desc}</p>
                <span className="mt-3 inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-primary">
                  {c.cta} <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
                </span>
              </>
            );
            return c.slug ? (
              <Link key={c.title} to="/learn/men-and-trauma/$slug" params={{ slug: c.slug }} className={startCardCls}>{inner}</Link>
            ) : (
               <Link key={c.title} to={c.to ?? "/learn/men-and-trauma"} className={startCardCls}>{inner}</Link>
            );
          })}
        </div>
      </section>

      {GROUPS.map((g) => <GroupCard key={g.title} g={g} />)}

      <section id="tools" className="mt-10 scroll-mt-24">
        <div className="px-1">
          <h2 className="font-serif text-3xl leading-tight text-foreground">Practical Tools</h2>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-foreground/75">Simple ways to reconnect with your body, release tension and bring your attention back to the present.</p>
        </div>
        <div className="-mx-4 mt-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:grid lg:grid-cols-3 lg:overflow-visible lg:px-0">
          {MEN_TOOLS.map((t) => {
            const theme = TOOL_CARD_THEME[t.id] ?? TOOL_CARD_THEME["tool-shop"];
            return (
              <Link
                key={t.id}
                to="/learn/men-and-trauma/tools"
                hash={t.id}
                className={`group flex w-[84%] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:w-[47%] lg:w-auto ${theme.shell}`}
              >
                <div className="aspect-[3/2] overflow-hidden bg-tool-paper">
                  <img src={t.image} alt={t.alt} loading="lazy" width={1200} height={800} className="h-full w-full object-cover transition-transform duration-500 motion-reduce:transition-none group-hover:scale-[1.025]" />
                </div>
                <div className="flex min-h-[230px] flex-1 flex-col bg-card/95 p-5 sm:p-6">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/65">{theme.label}</span>
                  <h3 className="mt-2 font-serif text-2xl leading-tight text-foreground">{t.title}</h3>
                  <p className="mt-3 flex-1 text-[15px] leading-relaxed text-foreground/75">{t.tagline}</p>
                  <span className={`mt-5 inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold ${theme.action}`}>
                    {t.cta} <ArrowRight className="h-4 w-4 shrink-0 transition-transform motion-reduce:transition-none group-hover:translate-x-1" aria-hidden />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <p className="mt-10 text-center font-serif text-lg italic text-foreground/80">“{MEN_INTRO.tagline}”</p>
      <MenCrisisNote />
        </>
      )}
    </main>
  );
}
